import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../lib/api";
import Hls from "hls.js";

type Line = [[number, number], [number, number]];
type LinesV2 = { line1: Line; line2: Line };
type ActiveCam = { id: string };

function useQuery() {
  return useMemo(() => new URLSearchParams(window.location.search), []);
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export default function ThresholdConfigWindow() {
  const query = useQuery();
  const cameraFromQuery = query.get("camera") ?? "";

  // Video + overlay
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const refreshTimer = useRef<number | null>(null);

  // Natural video size
  const [naturalSize, setNaturalSize] = useState({ w: 1280, h: 720 });
  const naturalLocked = useRef(false);

  // Saved frame size (from threshold file) - for auto-scaling
  const [savedFrameSize, setSavedFrameSize] = useState<{ w: number; h: number } | null>(null);

  // Camera selection
  const [cameras, setCameras] = useState<ActiveCam[]>([]);
  const [cameraId, setCameraId] = useState(cameraFromQuery || "");
  const [camsLoading, setCamsLoading] = useState(false);

  // Camera Role
  const [cameraRole, setCameraRole] = useState<"entrance" | "exit">("entrance");

  // UI state
  const [status, setStatus] = useState(
    "Select a camera. This tool can load saved lines, let you adjust them, and save—no recalibration required."
  );
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  // Calibration (optional path still supported)
  const [refB64, setRefB64] = useState<string | null>(null);
  const [runs, setRuns] = useState(4);
  const [jobId, setJobId] = useState<string | null>(null);
  const [calState, setCalState] = useState<"idle" | "running" | "done" | "error">("idle");
  const [calProgress, setCalProgress] = useState(0);
  const [calMessage, setCalMessage] = useState("");

  // Lines
  const [suggested, setSuggested] = useState<LinesV2 | null>(null);
  const [active, setActive] = useState<LinesV2 | null>(null);

  // Dragging
  const dragging = useRef<null | "line1" | "line2">(null);
  const dragOffsetY = useRef(0);
  const [warnAdjusted, setWarnAdjusted] = useState(false);

  // Impact (optional)
  const [impact, setImpact] = useState<{
    line1?: { usable_rate?: number | null; n?: number };
    line2?: { usable_rate?: number | null; n?: number };
  } | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Track if threshold exists on server
  const [thresholdExists, setThresholdExists] = useState(false);

  // Auto-detect camera role from name
  useEffect(() => {
    if (!cameraId) return;

    const lowerName = cameraId.toLowerCase();
    if (lowerName.includes("entrance") || lowerName.includes("entry")) {
      setCameraRole("entrance");
    } else if (lowerName.includes("exit")) {
      setCameraRole("exit");
    }
  }, [cameraId]);

  // ---------- Helpers: normalize lines to current frame ----------
  function normalizeToFrame(src: any): LinesV2 | null {
    if (!src?.line1 || !src?.line2) return null;
    const y1 = Number(src.line1?.[0]?.[1]);
    const y2 = Number(src.line2?.[0]?.[1]);
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;

    let yy1 = clamp(Math.round(y1), 0, naturalSize.h - 1);
    let yy2 = clamp(Math.round(y2), 0, naturalSize.h - 1);
    if (yy2 <= yy1) yy2 = clamp(yy1 + 5, 0, naturalSize.h - 1);

    return {
      line1: [[0, yy1], [naturalSize.w - 1, yy1]],
      line2: [[0, yy2], [naturalSize.w - 1, yy2]],
    };
  }

  function normalizeExistingLinesToFrame() {
    if (active) setActive(normalizeToFrame({ line1: active.line1, line2: active.line2 }));
    if (suggested) setSuggested(normalizeToFrame({ line1: suggested.line1, line2: suggested.line2 }));
  }

  // ---------- Fetch active cameras ----------
  useEffect(() => {
    let mounted = true;
    async function loadCams() {
      setCamsLoading(true);
      try {
        const res = await apiFetch("/api/cameras/active");
        const data = await res.json();
        if (!res.ok) throw new Error(JSON.stringify(data));
        if (!mounted) return;

        setCameras(Array.isArray(data) ? data : []);
        if (!cameraId && Array.isArray(data) && data.length) {
          setCameraId(data[0].id);
        }
      } catch (e: any) {
        if (mounted) setStatus(`Failed to load cameras: ${e?.message ?? String(e)}`);
      } finally {
        if (mounted) setCamsLoading(false);
      }
    }
    loadCams();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- HLS session helpers ----------
  async function fetchHlsUrl(cam: string) {
    const res = await apiFetch(
      `/api/kvs/hls-url?stream=${encodeURIComponent(cam)}&mode=LIVE&max_frag=3`
    );
    if (!res.ok) throw new Error("hls-url fetch failed");
    const { url } = await res.json();
    return url as string;
  }

  function clearRefresh() {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }

  function scheduleRefresh(ms: number) {
    clearRefresh();
    refreshTimer.current = window.setTimeout(() => {
      startHls(true).catch(() => {});
    }, ms) as unknown as number;
  }

  async function startHls(_isRefresh = false) {
    const video = videoRef.current;
    if (!video || !cameraId) return;

    const url = await fetchHlsUrl(cameraId);

    video.muted = true;
    video.playsInline = true;
    (video as any).autoplay = true;

    if (!Hls.isSupported()) {
      return new Promise<void>((resolve) => {
        const onCanPlay = () => {
          setReady(true);
          video.removeEventListener("canplay", onCanPlay);
          resolve();
        };
        video.addEventListener("canplay", onCanPlay, { once: true });
        video.src = url;
        video.play().catch(() => {});
        scheduleRefresh(4 * 60_000);
      });
    }

    let hls = hlsRef.current;
    if (!hls) {
      hls = new Hls({ enableWorker: true, liveBackBufferLength: 30 });
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setReady(true);
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data?.fatal || data?.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
          startHls(true).catch(() => {});
        }
      });

      hls.attachMedia(video);
    }

    hls.loadSource(url);
    scheduleRefresh(4 * 60_000);
  }

  // Start/Restart HLS when camera changes
  useEffect(() => {
    setReady(false);
    startHls().catch((e) => setStatus(`HLS start failed: ${e?.message ?? e}`));
    return () => {
      clearRefresh();
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;

      const v = videoRef.current;
      if (v) {
        try {
          v.removeAttribute("src");
          v.load();
        } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  // When metadata arrives, lock natural size
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      if (!naturalLocked.current && v.videoWidth && v.videoHeight) {
        setNaturalSize({ w: v.videoWidth, h: v.videoHeight });
        naturalLocked.current = true;
      }
      syncCanvasToVideo();
      normalizeExistingLinesToFrame();
      drawOverlay();
    };
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep overlay sized to displayed video
  useEffect(() => {
    const onResize = () => {
      syncCanvasToVideo();
      drawOverlay();
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw overlay when lines change
  useEffect(() => {
    drawOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, suggested, naturalSize, ready]);

  // Re-load threshold when natural size is determined (handles late metadata)
  useEffect(() => {
    if (!cameraId || !naturalLocked.current) return;

    // If we have a saved frame size that differs, reload to trigger scaling
    if (savedFrameSize && (savedFrameSize.w !== naturalSize.w || savedFrameSize.h !== naturalSize.h)) {
      loadSavedThreshold(cameraId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturalSize.w, naturalSize.h]);

  // ---------- Geometry helpers (letterboxing) ----------
  function getDisplayBox() {
    const v = videoRef.current;
    if (!v) return null;
    const elRect = v.getBoundingClientRect();
    const elW = elRect.width;
    const elH = elRect.height;
    const nW = naturalSize.w || 1;
    const nH = naturalSize.h || 1;
    const scale = Math.min(elW / nW, elH / nH);
    const dispW = nW * scale;
    const dispH = nH * scale;
    const offX = (elW - dispW) / 2;
    const offY = (elH - dispH) / 2;
    return { elRect, dispW, dispH, offX, offY, scale };
  }

  function syncCanvasToVideo() {
    const v = videoRef.current,
      c = canvasRef.current;
    if (!v || !c) return;
    const rect = v.getBoundingClientRect();
    c.width = Math.max(1, Math.floor(rect.width));
    c.height = Math.max(1, Math.floor(rect.height));
  }

  function clientToVideoCoords(clientX: number, clientY: number) {
    const box = getDisplayBox();
    if (!box) return null;
    const { elRect, dispW, dispH, offX, offY, scale } = box;

    const relX = clientX - elRect.left - offX;
    const relY = clientY - elRect.top - offY;
    if (relX < 0 || relY < 0 || relX > dispW || relY > dispH) return null;

    const x = Math.round(relX / scale);
    const y = Math.round(relY / scale);
    return { x, y };
  }

  // ---------- Load saved threshold with auto-scaling ----------
  async function loadSavedThreshold(cam: string) {
    if (!cam) return;
    if (calState === "running") return;

    try {
      setStatus(`Loading saved threshold for ${cam}…`);
      const res = await apiFetch(`/threshold_zones/${encodeURIComponent(cam)}`);
      const data = await res.json();

      if (!res.ok) {
        setSuggested(null);
        setActive(null);
        setWarnAdjusted(false);
        setImpact(null);
        setThresholdExists(false);
        setSavedFrameSize(null);
        setStatus(`No saved threshold for ${cam}. You can run calibration.`);
        return;
      }

      // Threshold exists on server
      setThresholdExists(true);

      // Capture saved frame size for auto-scaling
      const savedFrame = data?.frame;
      if (savedFrame?.w && savedFrame?.h) {
        setSavedFrameSize({ w: savedFrame.w, h: savedFrame.h });
      } else {
        setSavedFrameSize(null);
      }

      // V2 preferred
      const v2Sug = data?.lines?.suggested;
      const v2Act = data?.lines?.active;

      // Check if we need to scale due to resolution change
      const needsScaling =
        savedFrame?.w &&
        savedFrame?.h &&
        (savedFrame.w !== naturalSize.w || savedFrame.h !== naturalSize.h);

      if (needsScaling) {
        const scaleX = naturalSize.w / savedFrame.w;
        const scaleY = naturalSize.h / savedFrame.h;

        console.log(
          `[Threshold] Resolution changed: ${savedFrame.w}x${savedFrame.h} → ${naturalSize.w}x${naturalSize.h}, scaling by ${scaleY.toFixed(3)}`
        );

        setStatus(
          `Resolution changed (${savedFrame.w}x${savedFrame.h} → ${naturalSize.w}x${naturalSize.h}). ` +
            `Lines auto-scaled. Please verify and Save.`
        );

        const scaleLines = (src: any): LinesV2 | null => {
          if (!src?.line1 || !src?.line2) return null;
          const y1 = Number(src.line1?.[0]?.[1]);
          const y2 = Number(src.line2?.[0]?.[1]);
          if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;

          // Scale Y coordinates proportionally
          let yy1 = clamp(Math.round(y1 * scaleY), 0, naturalSize.h - 1);
          let yy2 = clamp(Math.round(y2 * scaleY), 0, naturalSize.h - 1);
          if (yy2 <= yy1) yy2 = clamp(yy1 + 5, 0, naturalSize.h - 1);

          return {
            line1: [[0, yy1], [naturalSize.w - 1, yy1]],
            line2: [[0, yy2], [naturalSize.w - 1, yy2]],
          };
        };

        const sug = scaleLines(v2Sug);
        const act = scaleLines(v2Act);

        if (act) {
          setActive(act);
          setSuggested(sug ?? act);
          setWarnAdjusted(true); // Mark as adjusted since we scaled
          setImpact(null);
          return;
        }
      }

      // No scaling needed - use normal normalization
      const sug = normalizeToFrame(v2Sug);
      const act = normalizeToFrame(v2Act);

      if (act) {
        setActive(act);
        setSuggested(sug ?? act);
        setWarnAdjusted(false);
        setImpact(null);
        setStatus(`Loaded saved threshold for ${cam}. Drag to adjust and Save.`);
        return;
      }

      // Legacy fallback: { line: [[x1,y],[x2,y]] }
      if (data?.line) {
        const y = Number(data.line?.[0]?.[1] ?? 0);

        // Check if legacy also needs scaling
        let yy = y;
        if (savedFrame?.h && savedFrame.h !== naturalSize.h) {
          const scaleY = naturalSize.h / savedFrame.h;
          yy = y * scaleY;
          setStatus(`Resolution changed. Legacy threshold auto-scaled. Please verify and Save.`);
          setWarnAdjusted(true);
        }

        yy = clamp(Math.round(yy), 0, naturalSize.h - 1);

        const lines: LinesV2 = {
          line1: [[0, yy], [naturalSize.w - 1, yy]],
          line2: [[0, yy], [naturalSize.w - 1, yy]],
        };
        setActive(lines);
        setSuggested(lines);
        if (!savedFrame?.h || savedFrame.h === naturalSize.h) {
          setWarnAdjusted(false);
        }
        setImpact(null);
        if (!savedFrame?.h || savedFrame.h === naturalSize.h) {
          setStatus(`Loaded legacy threshold for ${cam}. Drag to adjust and Save.`);
        }
        return;
      }

      setSuggested(null);
      setActive(null);
      setWarnAdjusted(false);
      setImpact(null);
      setStatus(`Saved threshold for ${cam} is an unsupported format.`);
    } catch (e: any) {
      setSuggested(null);
      setActive(null);
      setWarnAdjusted(false);
      setThresholdExists(false);
      setSavedFrameSize(null);
      setImpact(null);
      setStatus(`Failed to load saved threshold: ${e?.message ?? String(e)}`);
    }
  }

  // Auto-load saved threshold on open + camera change
  useEffect(() => {
    if (!cameraId) return;
    loadSavedThreshold(cameraId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  // ---------- Reference upload ----------
  async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error("Failed to read file"));
      r.readAsDataURL(file);
    });
  }

  // Calibration with camera_role
  async function startCalibration() {
    if (!cameraId) {
      setStatus("Pick a camera first.");
      return;
    }
    if (!refB64) {
      setStatus("Upload a reference face image first.");
      return;
    }

    setCalState("running");
    setCalProgress(0);
    setCalMessage("Starting...");
    setStatus("Calibration running… do 3–5 passes through the corridor.");
    setSuggested(null);
    setActive(null);
    setImpact(null);
    setWarnAdjusted(false);

    try {
      const res = await apiFetch("/api/calibration/start", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          camera: cameraId,
          camera_role: cameraRole,
          reference_image_b64: refB64,
          runs: runs,
          max_seconds: 180,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(data));
      setJobId(data.job_id);
    } catch (e: any) {
      setCalState("error");
      setStatus(`Calibration start failed: ${e?.message ?? String(e)}`);
    }
  }

  // Poll status and fetch result when done
  useEffect(() => {
    if (!jobId) return;

    let stopped = false;
    const timer = window.setInterval(async () => {
      if (stopped) return;
      try {
        const sRes = await apiFetch(`/api/calibration/status?job_id=${encodeURIComponent(jobId)}`);
        const sData = await sRes.json();
        if (!sRes.ok) throw new Error(JSON.stringify(sData));

        setCalMessage(sData.message ?? "");
        setCalProgress(Number(sData.progress ?? 0));

        const state = String(sData.state ?? "running");
        if (state === "done") {
          setCalState("done");
          setStatus("Calibration complete. You can adjust lines (optional) and Save.");

          const rRes = await apiFetch(`/api/calibration/result?job_id=${encodeURIComponent(jobId)}`);
          const rData = await rRes.json();
          if (!rRes.ok) throw new Error(JSON.stringify(rData));

          const sug = normalizeToFrame(rData?.lines?.suggested);
          if (sug) {
            setSuggested(sug);
            setActive(sug);
            setWarnAdjusted(false);
          }

          if (rData?.impact) setImpact(rData.impact);

          window.clearInterval(timer);
          stopped = true;
        } else if (state === "error") {
          setCalState("error");
          setStatus(`Calibration failed: ${sData.message ?? "unknown error"}`);
          window.clearInterval(timer);
          stopped = true;
        } else {
          setCalState("running");
        }
      } catch (e: any) {
        setStatus(`Calibration polling error: ${e?.message ?? String(e)}`);
      }
    }, 800);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // ---------- Drag-to-adjust ----------
  function pickLineNearY(y: number): null | "line1" | "line2" {
    if (!active) return null;
    const y1 = active.line1[0][1];
    const y2 = active.line2[0][1];
    const tol = 10;
    const d1 = Math.abs(y - y1);
    const d2 = Math.abs(y - y2);
    if (d1 <= tol && d1 <= d2) return "line1";
    if (d2 <= tol) return "line2";
    return null;
  }

  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!active) return;
    const pt = clientToVideoCoords(e.clientX, e.clientY);
    if (!pt) return;
    const which = pickLineNearY(pt.y);
    if (!which) return;
    dragging.current = which;
    dragOffsetY.current = pt.y - active[which][0][1];
  }

  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!active) return;
    if (!dragging.current) return;

    const pt = clientToVideoCoords(e.clientX, e.clientY);
    if (!pt) return;

    const which = dragging.current;
    const newY = clamp(pt.y - dragOffsetY.current, 0, naturalSize.h - 1);

    let y1 = active.line1[0][1];
    let y2 = active.line2[0][1];

    if (which === "line1") y1 = Math.min(newY, y2 - 5);
    if (which === "line2") y2 = Math.max(newY, y1 + 5);

    const next: LinesV2 = {
      line1: [[0, y1], [naturalSize.w - 1, y1]],
      line2: [[0, y2], [naturalSize.w - 1, y2]],
    };

    setActive(next);

    const baseline = suggested ?? next;
    const changed =
      baseline.line1[0][1] !== next.line1[0][1] || baseline.line2[0][1] !== next.line2[0][1];
    setWarnAdjusted(changed);
  }

  function onMouseUp() {
    dragging.current = null;
  }

  // ---------- Save (writes V2 JSON) ----------
  async function handleSave() {
    if (!cameraId) {
      setStatus("Pick a camera first.");
      return;
    }
    if (!active) {
      setStatus("No active lines loaded. Load saved threshold or run calibration first.");
      return;
    }
    if (!cameraRole) {
      setStatus("Camera role not set. Please select entrance or exit.");
      return;
    }

    const baseline = suggested ?? active;

    const payload = {
      version: 3,
      camera_role: cameraRole,
      frame: { w: naturalSize.w, h: naturalSize.h },
      lines: {
        trigger: active.line1,
        threshold: active.line2,
        suggested: { line1: baseline.line1, line2: baseline.line2 },
        active: { line1: active.line1, line2: active.line2 },
      },
      line_gap_px: Math.abs(active.line2[0][1] - active.line1[0][1]),
      hysteresis_px: 4,
    };

    setSaving(true);
    try {
      const res = await apiFetch(`/threshold_zones/${encodeURIComponent(cameraId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(data));
      setStatus(
        `Saved for ${cameraId} ✅ active=${String(data.active)} keys=${(data.keys ?? []).join(",")}`
      );
      setSuggested(active);
      setWarnAdjusted(false);
      setSavedFrameSize({ w: naturalSize.w, h: naturalSize.h }); // Update saved frame size
    } catch (e: any) {
      setStatus(`Save failed: ${e?.message ?? String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteThreshold() {
    if (!cameraId) return;

    setDeleting(true);
    try {
      const res = await apiFetch(`/threshold_zones/${encodeURIComponent(cameraId)}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }

      setSuggested(null);
      setActive(null);
      setWarnAdjusted(false);
      setImpact(null);
      setThresholdExists(false);
      setSavedFrameSize(null);
      setShowDeleteConfirm(false);
      setStatus(`Threshold deleted for ${cameraId}. Detections paused until new threshold is saved.`);
    } catch (e: any) {
      setStatus(`Delete failed: ${e?.message ?? String(e)}`);
    } finally {
      setDeleting(false);
    }
  }

  function drawOverlay() {
    const c = canvasRef.current;
    const v = videoRef.current;
    if (!c || !v) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, c.width, c.height);

    const box = getDisplayBox();
    if (!box) return;
    const { offX, offY, scale } = box;

    const drawLine = (line: Line, color: string, label: string) => {
      const y = line[0][1];
      const sy = Math.round(offY + y * scale);
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(Math.round(offX + 0 * scale), sy);
      ctx.lineTo(Math.round(offX + (naturalSize.w - 1) * scale), sy);
      ctx.stroke();

      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(10, sy - 22, 250, 22);
      ctx.fillStyle = "white";
      ctx.font = "14px sans-serif";
      ctx.fillText(`${label}  y=${y}`, 16, sy - 6);
    };

    if (suggested) {
      drawLine(suggested.line1, "rgba(255,255,255,0.35)", "Suggested Line 1");
      drawLine(suggested.line2, "rgba(255,255,255,0.35)", "Suggested Line 2");
    }

    if (active) {
      drawLine(active.line1, "rgba(0,255,255,0.95)", "Active Line 1 (start)");
      drawLine(active.line2, "rgba(255,80,80,0.95)", "Active Line 2 (final)");
    }
  }

  return (
    <div className="w-full h-[calc(100vh-56px)] bg-zinc-900 text-zinc-100 flex flex-col">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-3">Delete Threshold?</h3>
            <p className="text-zinc-300 mb-2">
              This will delete the saved threshold for <strong className="text-white">{cameraId}</strong>.
            </p>
            <p className="text-amber-400 text-sm mb-4">
              ⚠️ Detections will be paused for this camera until a new threshold is saved.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                className="px-4 py-2 rounded bg-zinc-700 hover:bg-zinc-600 text-white"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                onClick={handleDeleteThreshold}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete Threshold"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 py-2 border-b border-zinc-800 flex items-center gap-3 flex-wrap">
        <div className="font-semibold">
          Calibration Threshold — {cameraId || "(no camera selected)"}
        </div>
        <div className="text-sm opacity-80">{status}</div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* Camera dropdown */}
          <select
            className="px-2 py-1 rounded bg-zinc-800 border border-zinc-700"
            value={cameraId}
            disabled={camsLoading || calState === "running"}
            onChange={(e) => {
              const cam = e.target.value;
              setCameraId(cam);

              setImpact(null);
              setJobId(null);
              setCalState("idle");
              setCalProgress(0);
              setCalMessage("");
              setWarnAdjusted(false);

              setSuggested(null);
              setActive(null);
              setSavedFrameSize(null);

              setStatus("Loading saved threshold…");
              loadSavedThreshold(cam);
            }}
          >
            <option value="" disabled>
              {camsLoading ? "Loading cameras..." : "Select camera"}
            </option>
            {cameras.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id}
              </option>
            ))}
          </select>

          {/* Camera Role Selector */}
          <div className="flex items-center gap-1.5 bg-zinc-800 px-2 py-1 rounded border border-zinc-700">
            <span className="text-xs text-zinc-400">Role:</span>
            <button
              onClick={() => setCameraRole("entrance")}
              disabled={calState === "running"}
              className={`px-2.5 py-0.5 rounded text-xs transition-colors ${
                cameraRole === "entrance"
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Entrance
            </button>
            <button
              onClick={() => setCameraRole("exit")}
              disabled={calState === "running"}
              className={`px-2.5 py-0.5 rounded text-xs transition-colors ${
                cameraRole === "exit"
                  ? "bg-green-600 text-white"
                  : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Exit
            </button>
          </div>

          {/* Reload saved */}
          <button
            className="px-3 py-1 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50"
            disabled={!cameraId || calState === "running"}
            onClick={() => loadSavedThreshold(cameraId)}
          >
            Reload saved
          </button>

          {/* Delete Button */}
          <button
            className="px-3 py-1 rounded bg-red-700 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            disabled={!cameraId || !thresholdExists || calState === "running" || deleting}
            onClick={() => setShowDeleteConfirm(true)}
            title={thresholdExists ? "Delete saved threshold to pause detections" : "No threshold to delete"}
          >
            Delete threshold
          </button>

          {/* Reference image */}
          <label className="px-3 py-1 rounded bg-zinc-800 border border-zinc-700 cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={calState === "running"}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const b64 = await fileToBase64(f);
                setRefB64(b64);
                setStatus("Reference uploaded. Start calibration when ready (optional).");
              }}
            />
            {refB64 ? "Reference ✅" : "Upload reference"}
          </label>

          {/* Runs */}
          <select
            className="px-2 py-1 rounded bg-zinc-800 border border-zinc-700"
            value={runs}
            disabled={calState === "running"}
            onChange={(e) => setRuns(Number(e.target.value))}
          >
            {[3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n} runs
              </option>
            ))}
          </select>

          {/* Start calibration */}
          <button
            className="px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50"
            disabled={!cameraId || !refB64 || calState === "running"}
            onClick={startCalibration}
          >
            {calState === "running" ? "Calibrating..." : "Start calibration"}
          </button>

          {/* Save */}
          <button
            className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
            disabled={saving || !active}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save active lines"}
          </button>
        </div>

        {/* Calibration progress */}
        {calState === "running" && (
          <div className="w-full mt-2 flex items-center gap-3">
            <div className="text-sm opacity-80">{calMessage}</div>
            <div className="flex-1 h-2 bg-zinc-800 rounded">
              <div
                className="h-2 bg-emerald-500 rounded"
                style={{ width: `${Math.round(calProgress * 100)}%` }}
              />
            </div>
            <div className="text-sm w-12 text-right">{Math.round(calProgress * 100)}%</div>
          </div>
        )}

        {/* Camera role explanation */}
        <div className="w-full mt-2 text-xs bg-zinc-800 px-3 py-2 rounded border border-zinc-700">
          <span className="text-zinc-400">Camera Role: </span>
          {cameraRole === "entrance" ? (
            <span className="text-zinc-200">
              This camera monitors people <strong className="text-blue-400">entering</strong> the building.
              Crossings will be recorded as entries (is_inside=True).
            </span>
          ) : (
            <span className="text-zinc-200">
              This camera monitors people <strong className="text-green-400">exiting</strong> the building.
              Crossings will be recorded as exits (is_inside=False).
            </span>
          )}
        </div>

        {/* Resolution scaling warning */}
        {savedFrameSize && (savedFrameSize.w !== naturalSize.w || savedFrameSize.h !== naturalSize.h) && (
          <div className="w-full mt-2 text-xs bg-amber-900/50 px-3 py-2 rounded border border-amber-600">
            <span className="text-amber-400 font-semibold">⚠️ Resolution Changed: </span>
            <span className="text-amber-200">
              Threshold was saved at {savedFrameSize.w}x{savedFrameSize.h}, current stream is{" "}
              {naturalSize.w}x{naturalSize.h}. Lines have been auto-scaled.{" "}
              <strong>Please verify positions and Save.</strong>
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 grid place-items-center p-4">
        <div className="relative max-w-[90vw] max-h-[75vh]">
          <video
            ref={videoRef}
            className="block max-w-full max-h-[75vh] object-contain rounded bg-black"
            playsInline
            muted
            autoPlay
            onLoadedMetadata={() => {
              const v = videoRef.current;
              if (v && !naturalLocked.current && v.videoWidth && v.videoHeight) {
                setNaturalSize({ w: v.videoWidth, h: v.videoHeight });
                naturalLocked.current = true;
              }
              syncCanvasToVideo();
              normalizeExistingLinesToFrame();
              drawOverlay();
            }}
            onCanPlay={() => setReady(true)}
          />

          <canvas
            ref={canvasRef}
            className="absolute inset-0 z-10 cursor-ns-resize"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          />

          {!ready && (
            <div className="absolute inset-0 grid place-items-center text-gray-400">Loading…</div>
          )}
        </div>
      </div>
    </div>
  );
}